import '@fontsource/noto-kufi-arabic/300.css';
import '@fontsource/noto-kufi-arabic/400.css';
import '@fontsource/noto-kufi-arabic/500.css';
import '@fontsource/noto-kufi-arabic/700.css';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './styles/globals.css';

import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, DirectionProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/ar';

import { App } from './App';
import { theme } from './theme';
import { initI18n } from './i18n/init';

// Localizes every Mantine date control — without this, DatePickerInput
// renders English month names in the Arabic UI. useTranslation keeps the
// locale in sync when the user switches language (no page reload happens).
function LocalizedDatesProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <DatesProvider settings={{ locale: i18n.language === 'ar' ? 'ar' : 'en' }}>
      {children}
    </DatesProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found.');
}

// initI18n is async (loads stored language + applies document direction).
// Chained via .then() instead of top-level await so the build can target
// broader browsers (Safari 14, Chrome 87) without bumping esbuild.
void initI18n().then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <DirectionProvider initialDirection="rtl" detectDirection={false}>
        <MantineProvider theme={theme} defaultColorScheme="light">
          <LocalizedDatesProvider>
            <Notifications position="top-center" />
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </QueryClientProvider>
          </LocalizedDatesProvider>
        </MantineProvider>
      </DirectionProvider>
    </StrictMode>,
  );
});
